// hooks/use-voice.js — LAYER 3: VOICE ENGINE
// ============================================================================
// Owns: TTS (emilySpeak), STT (toggleVoice), wake word (startWakeListener)
// Calls: send() from use-send via callback
// Rule: Never touch use-send.js to add voice behavior. Voice logic lives here.
// ============================================================================

import { useState, useRef, useEffect } from 'react';

export function useVoice({ session, userRecord, currentSessionId, threadHistory, inputRef, setInput, send, voiceSourceRef, voiceResponseCallbackRef, voiceEnabled: voiceEnabledProp }) {

  // ── STATE ──────────────────────────────────────────────────────────────────
  const [voiceListening,  setVoiceListening]  = useState(false);
  const [wakeListening,   setWakeListening]   = useState(false);
  const [voiceConfirm,    setVoiceConfirm]    = useState(null);
  const [voiceActive,     setVoiceActive]     = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    if (voiceEnabledProp !== undefined) return voiceEnabledProp;
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem('shift_voice_enabled');
    return stored === null ? true : stored === 'true';
  });

  // ── REFS ───────────────────────────────────────────────────────────────────
  const voiceRef     = useRef(null);
  const voiceFinalRef = useRef('');
  const voiceTTSRef  = useRef(null);
  const wakeRef      = useRef(null);

  const voiceSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  useEffect(() => {
    if (voiceEnabledProp !== undefined) {
      setVoiceEnabled(voiceEnabledProp);
      if (!voiceEnabledProp) {
        // Full cleanup when voice turned off
        if (voiceRef.current) {
          voiceRef.current.stop();
          voiceRef.current = null;
        }
        cleanupAudio();
        setVoiceListening(false);
        if (voiceTTSRef.current) {
          window.speechSynthesis?.cancel();
          voiceTTSRef.current = null;
        }
      }
    }
  }, [voiceEnabledProp]);

  // ── TTS ────────────────────────────────────────────────────────────────────
  function emilySpeak(text, onDone) {
    if (!window.speechSynthesis) { onDone?.(); return; }
    window.speechSynthesis.cancel();
    const clean = text
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/`[^`]+`/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .trim();
    const sentences = clean.match(/[^.!?]+[.!?]+/g) || [clean];
    const speakText = sentences.slice(0, 3).join(' ');
    const utterance = new SpeechSynthesisUtterance(speakText);
    utterance.rate = 0.95;
    utterance.pitch = 1.05;
    utterance.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.name.includes('Samantha'))
      || voices.find(v => v.name.includes('Karen'))
      || voices.find(v => v.name.includes('Victoria'))
      || voices.find(v => v.lang.startsWith('en') && v.default)
      || voices.find(v => v.lang.startsWith('en'));
    if (preferred) utterance.voice = preferred;
    utterance.onend = () => { voiceTTSRef.current = null; onDone?.(); };
    utterance.onerror = () => { voiceTTSRef.current = null; onDone?.(); };
    voiceTTSRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }

  function stopTTS() {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    voiceTTSRef.current = null;
  }

  // ── END VOICE SESSION ─────────────────────────────────────────────────────
  function endVoiceSession() {
    setVoiceActive(false);
    stopTTS();
    voiceRef.current?.stop();
    voiceRef.current = null;
    cleanupAudio();
    setVoiceListening(false);
    voiceResponseCallbackRef.current = null;
  }

  // ── Audio cleanup ref — persists across toggle cycles ──────────────────────
  const audioCleanupRef = useRef(null);

  function cleanupAudio() {
    if (audioCleanupRef.current) {
      const { stream, ctx, interval } = audioCleanupRef.current;
      if (interval) clearInterval(interval);
      try { stream?.getTracks().forEach(t => t.stop()); } catch (_) {}
      try { ctx?.close(); } catch (_) {}
      audioCleanupRef.current = null;
    }
  }

  // ── STT — toggle mic ──────────────────────────────────────────────────────
  function toggleVoice() {
    if (voiceListening) {
      // Stop: clean up everything immediately
      voiceRef.current?.stop();
      voiceRef.current = null;
      cleanupAudio();
      setVoiceListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    // Clean up any leftover from previous session
    cleanupAudio();
    if (voiceRef.current) {
      try { voiceRef.current.stop(); } catch (_) {}
      voiceRef.current = null;
    }

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    voiceFinalRef.current = '';
    voiceRef.current = rec;

    let audioCtx = null, analyser = null, audioStream = null;
    let sampleInterval = null;
    let wordCount = 0, pauseCount = 0, lastSpeechTime = Date.now(), inPause = false;
    const rmsValues = [];
    const hesitationWords = ['um','uh','like','i mean','you know','sort of','kind of'];
    let hesitationCount = 0;
    const emphasisWords = [];

    navigator.mediaDevices?.getUserMedia({ audio: true }).then(stream => {
      audioStream = stream;
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      // Store for cleanup
      audioCleanupRef.current = { stream, ctx: audioCtx, interval: null };
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      sampleInterval = setInterval(() => {
        if (!voiceRef.current) { clearInterval(sampleInterval); return; }
        analyser.getFloatTimeDomainData(buf);
        const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length);
        rmsValues.push(rms);
        if (rms < 0.01) { if (!inPause) { inPause = true; pauseCount++; } }
        else { inPause = false; }
      }, 200);
      if (audioCleanupRef.current) audioCleanupRef.current.interval = sampleInterval;
    }).catch(() => {});

    let silenceTimer = null;
    const SILENCE_INITIAL_MS = 4000;
    const SILENCE_AFTER_SPEECH_MS = 2200;
    let hasSpeechStarted = false;
    const startTime = Date.now();
    let isGoodbyeEnd = false;

    function resetSilenceTimer() {
      if (silenceTimer) clearTimeout(silenceTimer);
      lastSpeechTime = Date.now();
      const delay = hasSpeechStarted ? SILENCE_AFTER_SPEECH_MS : SILENCE_INITIAL_MS;
      silenceTimer = setTimeout(() => { rec.stop(); }, delay);
    }

    rec.onresult = (e) => {
      if (!hasSpeechStarted) hasSpeechStarted = true;
      resetSilenceTimer();
      let interim = '';
      let final = voiceFinalRef.current;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          final += (final ? ' ' : '') + t;
          wordCount += t.split(/\s+/).filter(Boolean).length;
          const lower = t.toLowerCase();
          hesitationWords.forEach(h => { if (lower.includes(h)) hesitationCount++; });
          const caps = t.match(/\b[A-Z]{2,}\b/g);
          if (caps) emphasisWords.push(...caps.map(w => w.toLowerCase()));
          if (/\bgoodbye\b|\bgood\s+bye\b|\bbye\b|\bdone\b|\bthat'?s\s+all\b|\bstop\b|\bend\b/.test(lower)) {
            const goodbyeMatch = t.match(/^(.+?)\s*\b(?:goodbye|good\s+bye|bye|done|that'?s\s+all|stop|end)\b/i);
            const preGoodbye = goodbyeMatch ? goodbyeMatch[1].trim() : '';
            const fullPreGoodbye = (voiceFinalRef.current + (preGoodbye ? ' ' + preGoodbye : '')).trim();
            isGoodbyeEnd = true;
            voiceFinalRef.current = fullPreGoodbye;
            if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
            voiceRef.current = null;
            try { rec.stop(); } catch (_) {}
            return;
          }
        } else { interim = t; }
      }
      voiceFinalRef.current = final;
      if (inputRef.current) inputRef.current.innerText = final + (interim ? ' ' + interim : '');
    };

    rec.onend = async () => {
      // If manually stopped (voiceRef cleared), don't restart
      if (!voiceRef.current) {
        if (silenceTimer) clearTimeout(silenceTimer);
        cleanupAudio();
        setVoiceListening(false);
        // still process transcript below
      } else if (silenceTimer && voiceRef.current === rec) {
        // Silence timeout triggered stop — try to restart for continuous listening
        try { rec.start(); return; } catch (_) {}
      }
      if (silenceTimer) clearTimeout(silenceTimer);
      setVoiceListening(false);
      cleanupAudio();

      const transcript = voiceFinalRef.current.trim();
      if (!transcript) {
        if (isGoodbyeEnd) { emilySpeak('Talk soon.', () => endVoiceSession()); }
        return;
      }

      const lower = transcript.toLowerCase().trim();
      const isGoodbye = /\bgoodbye\b|\bgood\s+bye\b|\bbye\b|\bdone\b|\bthat'?s\s+all\b|\bstop\b|\bend\b/.test(lower);
      if (isGoodbye) { emilySpeak('Talk soon.', () => endVoiceSession()); return; }

      const durationSeconds = (Date.now() - startTime) / 1000;
      const avgRms = rmsValues.length > 0 ? rmsValues.reduce((s, v) => s + v, 0) / rmsValues.length : 0;
      const wpm = durationSeconds > 0 ? Math.round((wordCount / durationSeconds) * 60) : 0;
      const acousticAnalysis = {
        duration_seconds: Math.round(durationSeconds),
        word_count: wordCount,
        words_per_minute: wpm,
        pace: wpm > 160 ? 'fast' : wpm > 100 ? 'medium' : 'slow',
        energy: avgRms > 0.05 ? 'high' : avgRms > 0.02 ? 'medium' : 'low',
        pause_count: pauseCount,
        hesitation_count: hesitationCount,
        emphasis_words: emphasisWords.slice(0, 5),
      };

      try {
        const threadTitles = threadHistory
          .filter(t => t.what_shifted || t.summary)
          .slice(0, 10)
          .map(t => ({ id: t.id, title: t.what_shifted || t.summary || 'Untitled thread' }));

        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'classifyVoice', transcript, threadTitles,
            acousticAnalysis, sessionId: currentSessionId,
            userId: session?.user?.id,
          }),
        });
        const data = await res.json();
        const chatSeg = data.segments?.find(s => s.route === 'chat' || s.route === 'chat_companion');

        setVoiceConfirm({ ...data, acousticAnalysis });

        if (chatSeg) {
          if (inputRef.current) inputRef.current.innerText = chatSeg.synthesis;
          setInput(chatSeg.synthesis);
          voiceSourceRef.current = true;
          voiceResponseCallbackRef.current = (responseText) => {
            voiceResponseCallbackRef.current = null;
            if (!responseText) return;
            if (isGoodbyeEnd) {
              emilySpeak(responseText, () => setTimeout(() => { emilySpeak('Talk soon.', () => endVoiceSession()); }, 200));
            } else {
              emilySpeak(responseText, () => {
                if (voiceActive) setTimeout(() => toggleVoice(), 500);
              });
            }
          };
          setTimeout(() => send(), 120);
        } else {
          const ack = data.distribution?.length > 0
            ? `Got it. Saved ${data.distribution.length === 1 ? 'that' : data.distribution.length + ' things'}.`
            : 'Got it.';
          if (isGoodbyeEnd) {
            emilySpeak(ack + ' Talk soon.', () => endVoiceSession());
          } else {
            emilySpeak(ack, () => {
              if (voiceActive) setTimeout(() => toggleVoice(), 400);
            });
          }
        }
      } catch (_) {
        if (inputRef.current) { inputRef.current.innerText = transcript; setInput(transcript); }
        if (isGoodbyeEnd) { endVoiceSession(); }
        else if (voiceActive) setTimeout(() => toggleVoice(), 800);
      }
    };

    rec.onerror = (e) => {
      if (e.error === 'no-speech') {
        if (!hasSpeechStarted && (Date.now() - startTime) < SILENCE_INITIAL_MS) {
          try { rec.start(); } catch (_) {}
        }
        return;
      }
      if (e.error !== 'aborted') {
        if (silenceTimer) clearTimeout(silenceTimer);
        setVoiceListening(false);
      }
    };

    silenceTimer = setTimeout(() => {
      if (!hasSpeechStarted) rec.stop();
    }, SILENCE_INITIAL_MS);

    // Small delay — Chrome needs time to release previous audio session
    setTimeout(() => {
      try { rec.start(); } catch (_) { console.warn('rec.start failed'); }
      setVoiceListening(true);
    }, 150);
  }

  // ── WAKE WORD ─────────────────────────────────────────────────────────────
  function normalizeForWake(text) {
    return text.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  }

  function wakeWordDetected(transcript, aiName) {
    if (!aiName) return false;
    const t = normalizeForWake(transcript);
    const name = normalizeForWake(aiName);
    const pattern = new RegExp(`hey\\s+\\S*\\s*${name}|hey\\s+${name}`, 'i');
    return pattern.test(t) || (t.includes('hey') && t.includes(name));
  }

  function startWakeListener(aiName) {
    if (!voiceSupported || !aiName) return;
    if (wakeRef.current) return;
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const wake = new SR();
      wake.continuous = true;
      wake.interimResults = true;
      wake.lang = 'en-US';
      wake.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const text = e.results[i][0].transcript;
          if (wakeWordDetected(text, aiName)) {
            stopWakeListener();
            setVoiceActive(true);
            stopTTS();
            toggleVoice();
            return;
          }
        }
      };
      wake.onend = () => {
        if (wakeRef.current === wake && !voiceListening) {
          try { wake.start(); } catch (_) {}
        }
      };
      wake.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          stopWakeListener(); return;
        }
        if (wakeRef.current === wake) {
          setTimeout(() => { try { wake.start(); } catch (_) {} }, 1000);
        }
      };
      wake.start();
      wakeRef.current = wake;
      setWakeListening(true);
    } catch (_) {}
  }

  function stopWakeListener() {
    if (wakeRef.current) {
      try { wakeRef.current.abort(); } catch (_) {}
      wakeRef.current = null;
    }
    setWakeListening(false);
    setVoiceActive(false);
  }

  // ── AUTO-START WAKE LISTENER when voice is enabled ─────────────────────────
  // "Hey {ai_name}" activates recording from any screen
  useEffect(() => {
    const aiName = userRecord?.ai_name;
    if (voiceEnabled && aiName && voiceSupported && !wakeListening && !voiceListening) {
      startWakeListener(aiName);
    }
    if (!voiceEnabled && wakeListening) {
      stopWakeListener();
    }
    return () => { if (wakeListening) stopWakeListener(); };
  }, [voiceEnabled, userRecord?.ai_name, voiceSupported]);

  // ── RETURN ─────────────────────────────────────────────────────────────────
  return {
    // State
    voiceListening, setVoiceListening,
    wakeListening,  setWakeListening,
    voiceConfirm,   setVoiceConfirm,
    voiceActive,    setVoiceActive,
    voiceEnabled,   setVoiceEnabled,
    voiceSupported,
    voiceTTSRef,
    // Functions
    emilySpeak,
    stopTTS,
    endVoiceSession,
    toggleVoice,
    startWakeListener,
    stopWakeListener,
  };
}
